<template>
  <div class="animated fadeIn support-user-page">
    <b-row>
      <b-col cols="12" lg="5">
        <b-card>
          <template #header>
            <h5 class="m-0">Solicitar suporte</h5>
          </template>
          <b-form @submit.prevent="submitTicket">
            <b-form-group label="Modulo" label-for="support-module">
              <b-form-select
                id="support-module"
                v-model="form.module"
                :options="moduleOptions"
                required
              />
            </b-form-group>

            <b-form-group label="Assunto" label-for="support-title">
              <b-form-input
                id="support-title"
                v-model="form.title"
                placeholder="Resumo rapido do problema"
              />
            </b-form-group>

            <b-form-group label="Descricao" label-for="support-description">
              <b-form-textarea
                id="support-description"
                v-model="form.description"
                rows="6"
                required
                placeholder="Descreva em detalhes o que esta acontecendo"
              />
            </b-form-group>

            <b-form-group label="Anexo (opcional)">
              <b-form-file
                v-model="form.file"
                browse-text="Selecionar"
                placeholder="Imagem ou documento"
              />
            </b-form-group>

            <b-button type="submit" variant="primary" :disabled="submitting">
              <b-spinner v-if="submitting" small class="mr-1" />
              Enviar solicitacao
            </b-button>
          </b-form>
        </b-card>
      </b-col>

      <b-col cols="12" lg="7">
        <b-card>
          <template #header>
            <h5 class="m-0">Meus protocolos</h5>
          </template>
          <b-card-body>
            <div class="filters-section">
              <b-form-group label="Pesquisar" class="search-filter">
                <b-input-group>
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <i class="fas fa-search" aria-hidden="true"></i>
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="filterProtocol"
                    type="search"
                    placeholder="Digite para pesquisar protocolo"
                    @input="debounceFilter"
                    @keyup.enter="applyFilter"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button :disabled="!filterProtocol" @click="clearFilter"
                      >Limpar</b-button
                    >
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </div>

            <div class="table-container">
              <b-table
                :items="tickets.docs"
                :fields="ticketFields"
                :busy="loading"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :tbody-tr-class="rowClass"
                thead-class="support-header"
                borderless
                hover
                responsive
                show-empty
                no-local-sorting
                @sort-changed="onSortChanged"
                @row-clicked="openTicket"
              >
                <template #cell(protocol)="slotData">
                  <strong class="protocol-link">{{
                    slotData.item.protocol
                  }}</strong>
                </template>
                <template #cell(status)="slotData">
                  <b-badge :variant="statusVariant(slotData.item.status)">
                    {{ statusLabel(slotData.item.status) }}
                  </b-badge>
                </template>
                <template #cell(createdAt)="slotData">
                  <span class="date-text">{{
                    formatDate(slotData.item.createdAt)
                  }}</span>
                </template>

                <template #table-busy>
                  <div class="text-center py-5">
                    <b-spinner></b-spinner>
                    <span class="ml-2">Carregando...</span>
                  </div>
                </template>
                <template #empty>
                  <div class="text-muted text-center py-5">
                    <i class="fas fa-life-ring fa-2x"></i>
                    <p class="mt-3">Nenhum protocolo encontrado</p>
                  </div>
                </template>
              </b-table>
            </div>

            <nav class="pagination-nav">
              <b-pagination
                v-model="tickets.page"
                :total-rows="tickets.total"
                :per-page="tickets.limit"
                prev-text="Anterior"
                next-text="Próximo"
                align="center"
              />
            </nav>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { createSupportTicket, getMySupportTickets } from "../../api/support";

export default {
  name: "SupportUser",
  data() {
    return {
      loading: false,
      submitting: false,
      sendingMessage: false,
      sortBy: "createdAt",
      sortDesc: true,
      filterTimeout: null,
      filterProtocol: "",
      form: {
        module: "",
        title: "",
        description: "",
        file: null,
      },
      moduleOptions: [
        { value: "", text: "Selecione" },
        { value: "Denuncias", text: "Denuncias" },
        { value: "LGPD", text: "LGPD" },
        { value: "Ouvidoria", text: "Ouvidoria" },
        { value: "Duvidas e Sugestoes", text: "Duvidas e Sugestoes" },
        { value: "EAD", text: "EAD" },
        { value: "NR-1", text: "NR-1" },
        { value: "Outro", text: "Outro" },
      ],
      ticketFields: [
        { key: "protocol", label: "Protocolo", sortable: true },
        { key: "module", label: "Modulo", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "createdAt", label: "Data", sortable: true },
      ],
      tickets: {
        docs: [],
        page: 1,
        limit: 15,
        total: 0,
      },
    };
  },
  watch: {
    "tickets.page"() {
      this.fetchTickets();
    },
  },
  mounted() {
    this.fetchTickets();
  },
  beforeDestroy() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  },
  methods: {
    showSuccessToast(message, title = "Sucesso") {
      this.$bvToast.toast(message, {
        title,
        toastClass: "custom-toast custom-toast-success",
        variant: "light",
        solid: true,
        autoHideDelay: 5000,
        noCloseButton: false,
      });
    },
    showErrorToast(message, title = "Erro") {
      this.$bvToast.toast(message, {
        title,
        toastClass: "custom-toast custom-toast-error",
        variant: "light",
        solid: true,
        autoHideDelay: 10000,
        noCloseButton: false,
      });
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("pt-BR");
    },
    statusLabel(status) {
      const labels = {
        open: "Aberto",
        in_progress: "Em atendimento",
        waiting_customer: "Aguardando cliente",
        resolved: "Resolvido",
        closed: "Fechado",
      };
      return labels[status] || status;
    },
    statusVariant(status) {
      const variants = {
        open: "warning",
        in_progress: "primary",
        waiting_customer: "info",
        resolved: "success",
        closed: "secondary",
      };
      return variants[status] || "secondary";
    },
    rowClass(item) {
      if (!item) return "";
      const baseClass = "support-row";
      return item.status === "closed"
        ? `${baseClass} support-concluded`
        : baseClass;
    },
    onSortChanged(sortOptions) {
      if (sortOptions.sortBy) {
        this.sortBy = sortOptions.sortBy;
        this.sortDesc = sortOptions.sortDesc || false;
      } else {
        this.sortBy = null;
        this.sortDesc = false;
      }
      this.fetchTickets();
    },
    applyFilter() {
      this.tickets.page = 1;
      this.fetchTickets();
    },
    debounceFilter() {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.applyFilter();
      }, 300);
    },
    clearFilter() {
      this.filterProtocol = "";
      this.applyFilter();
    },
    async fetchTickets() {
      this.loading = true;
      try {
        const response = await getMySupportTickets({
          page: this.tickets.page,
          size: this.tickets.limit,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          protocol: this.filterProtocol || undefined,
        });
        this.tickets = {
          ...this.tickets,
          ...response.data,
        };
      } catch (error) {
        this.showErrorToast("Erro ao carregar protocolos de suporte");
      } finally {
        this.loading = false;
      }
    },
    async submitTicket() {
      this.submitting = true;
      try {
        const response = await createSupportTicket({
          module: this.form.module,
          title: this.form.title || "",
          description: this.form.description,
        });

        if (this.form.file) {
          const filePayload = new FormData();
          filePayload.append("text", "Anexo inicial do chamado");
          filePayload.append("file", this.form.file);
          await addSupportMessage(response.data._id, filePayload);
        }

        this.showSuccessToast(
          `Solicitacao enviada. Protocolo: ${response.data.protocol}`,
          "Sucesso"
        );

        this.form = {
          module: "",
          title: "",
          description: "",
          file: null,
        };

        await this.fetchTickets();
      } catch (error) {
        const message =
          (error && error.response && error.response.data) ||
          "Nao foi possivel enviar sua solicitacao";
        this.showErrorToast(message);
      } finally {
        this.submitting = false;
      }
    },
    async openTicket(ticket) {
      this.$router.push({ path: `/suporte/${ticket._id}` });
    },
  },
};
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
}

.search-filter {
  flex: 1;
}

.filters-section .form-group {
  margin-bottom: 0;
}

.support-header {
  background-color: #f8f9fa;
}

::v-deep .support-row:hover {
  cursor: pointer;
}

.protocol-link {
  color: #007bff;
  font-weight: 600;
}

.date-text {
  color: #6c757d;
  font-size: 0.875rem;
}

.pagination-nav {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
}
</style>
